import type { Attachment, LastMessageMedia } from '../api/types'
import { attachmentKind } from './files'

/** The longest clips the app sends (contract: "Attachments", length limits). The worker refuses more than 10% over. */
export const MAX_VIDEO_MS = 3 * 60_000
export const MAX_AUDIO_MS = 30 * 60_000
export const MAX_VOICE_MS = 5 * 60_000
/** Levels in a voice message's waveform. */
export const WAVEFORM_LEVELS = 64

/** "0:40", "12:34" or "1:02:03", to the nearest second; a clip shorter than a second is "0:01" (as the server says it). */
export function formatDuration(durationMs: number): string {
  const seconds = durationMs > 0 && durationMs < 1000 ? 1 : Math.round(Math.max(0, durationMs) / 1000)
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = String(seconds % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`
}

/** A length people are shown: none while it isn't known (null, or 0). */
export function knownDuration(durationMs: number | null | undefined): number | null {
  return typeof durationMs === 'number' && Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null
}

/** Whether an attachment was stored or sent before video and audio existed, so it lacks the media fields. */
export function needsMediaDefaults(attachment: Attachment): boolean {
  return attachment.kind === undefined || attachment.voice === undefined || attachment.durationMs === undefined || attachment.media === undefined || attachment.waveform === undefined
}

/**
 * An attachment with every field the app uses: the kind comes from the type, and the rest are none (contract:
 * "Event payloads").
 */
export function withAttachmentDefaults(attachment: Attachment): Attachment {
  if (!needsMediaDefaults(attachment)) return attachment
  return {
    ...attachment,
    kind: attachment.kind ?? attachmentKind(attachment.contentType),
    voice: attachment.voice ?? false,
    durationMs: attachment.durationMs ?? null,
    media: attachment.media ?? null,
    waveform: attachment.waveform ?? null,
  }
}

type ClipFields = Pick<Attachment, 'contentType' | 'fileName'> & Partial<Pick<Attachment, 'kind' | 'voice' | 'durationMs'>>

function kindOf(attachment: ClipFields): Attachment['kind'] {
  return attachment.kind ?? attachmentKind(attachment.contentType)
}

/** Video or audio. */
export function isClip(attachment: ClipFields): boolean {
  const kind = kindOf(attachment)
  return kind === 'video' || kind === 'audio'
}

/** A message's first video or audio attachment. */
export function firstClip<T extends ClipFields>(attachments: readonly T[]): T | undefined {
  return attachments.find(isClip)
}

const withDuration = (text: string, durationMs: number | null | undefined) => {
  const known = knownDuration(durationMs)
  return known === null ? text : `${text} (${formatDuration(known)})`
}

/** How previews describe a clip: "🎬 Video (0:40)", "🎤 Voice message (0:42)" or "🎵 {file name}". Null for others. */
export function describeClip(attachment: ClipFields): string | null {
  const kind = kindOf(attachment)
  if (kind === 'video') return withDuration('🎬 Video', attachment.durationMs)
  if (kind !== 'audio') return null
  return attachment.voice ? withDuration('🎤 Voice message', attachment.durationMs) : `🎵 ${attachment.fileName}`
}

/** A conversation's last message for the list, which has no file name: "🎵 Audio (3:12)" for an audio file. */
export function describeLastMedia(media: LastMessageMedia): string {
  if (media.kind === 'video') return withDuration('🎬 Video', media.durationMs)
  if (media.kind === 'voice') return withDuration('🎤 Voice message', media.durationMs)
  return withDuration('🎵 Audio', media.durationMs)
}

/** What a conversation's last message says about its clips (ChannelSummary.lastMessage.media). */
export function lastMessageMedia(attachments: readonly Attachment[]): LastMessageMedia | null {
  const clip = firstClip(attachments)
  if (!clip) return null
  return { kind: clip.voice ? 'voice' : kindOf(clip) === 'video' ? 'video' : 'audio', durationMs: knownDuration(clip.durationMs) }
}

/** What a clip is called in a sentence: "video", "voice message" or "audio file". */
function clipNoun(attachment: Attachment): string {
  if (kindOf(attachment) === 'video') return 'video'
  return attachment.voice ? 'voice message' : 'audio file'
}

/**
 * Why a message's clips stop it being forwarded, and whether trying again later can help; null when nothing does. The
 * server checks the message itself before any target (contract: "Forwarding"), so one clip that isn't converted refuses
 * the whole forward: a copy of it would hand the upload on to people who may not fetch it.
 */
export function clipForwardProblem(attachments: readonly Attachment[]): { message: string; retryable: boolean } | null {
  // A failed clip stays failed, so that is the answer even while another is still processing, as the server answers.
  const failed = attachments.find((a) => a.media === 'failed')
  if (failed) return { message: `This ${clipNoun(failed)} couldn't be prepared, so it can't be forwarded.`, retryable: false }
  const processing = attachments.find((a) => a.media === 'processing')
  return processing ? { message: `This ${clipNoun(processing)} is still being prepared. Try again in a moment.`, retryable: true } : null
}

/** Why a clip is too long to send, or null. A length the browser couldn't read (null) is left to the worker. */
export function lengthProblem(fileName: string, kind: Attachment['kind'], durationMs: number | null): string | null {
  if (durationMs === null || (kind !== 'video' && kind !== 'audio')) return null
  const limit = kind === 'video' ? MAX_VIDEO_MS : MAX_AUDIO_MS
  // As long as it reads: a 3:00.4 clip says 3:00.
  if (Math.round(durationMs / 1000) * 1000 <= limit) return null
  return `${fileName} is too long. ${kind === 'video' ? 'Videos can be up to 3 minutes' : 'Audio files can be up to 30 minutes'}.`
}
